import { walkTaxonomyTreeToSelectionLists } from '@revelio/data-access';
import type { TreeData } from '@revelio/ui';
import { normalizeSuggestedTitles } from '@revelio/ui';

import type { TaxonomyLevelMetadata } from './taxonomyTree';

export type ParentLinkedItem = {
  id: string;
  shortName: string;
  longName: string;
  nodeCount?: number;
  // Suggested titles from the taxonomy tree (when fetched with includeTitles,
  // e.g. role/skill trees). Exposed as `rawTitles` — the key the breadcrumb
  // tree maps to a node's suggestedTitles.
  rawTitles?: string[];
  // Each ancestor level's raw id, keyed by that ancestor's level tag — e.g. an
  // msa item carries both `region` and `country`; a rics_k400 item carries both
  // `rics_k10` and `rics_k50`. Consumers read whichever linkage they need.
  [ancestorLevelTag: string]: string | number | string[] | undefined;
};

type LevelKeyResolver = (level: TaxonomyLevelMetadata) => string | null;

type ParentLinkedSelectionLists = Partial<Record<string, ParentLinkedItem[]>>;

const keyByLevelTag: LevelKeyResolver = (level) => level.tag ?? null;

const selectionListsByTree = new WeakMap<
  readonly TreeData[],
  WeakMap<
    readonly TaxonomyLevelMetadata[],
    WeakMap<LevelKeyResolver, ParentLinkedSelectionLists>
  >
>();

const freezeSelectionLists = (lists: ParentLinkedSelectionLists) => {
  for (const list of Object.values(lists)) {
    if (!list) continue;
    for (const item of list) {
      if (Array.isArray(item.rawTitles)) Object.freeze(item.rawTitles);
      Object.freeze(item);
    }
    Object.freeze(list);
  }
  Object.freeze(lists);
};

const walkTree = (
  treeData: readonly TreeData[],
  levels: readonly TaxonomyLevelMetadata[],
  keyForLevel: LevelKeyResolver
): ParentLinkedSelectionLists =>
  walkTaxonomyTreeToSelectionLists<
    TreeData,
    ParentLinkedItem,
    TaxonomyLevelMetadata
  >({
    rootNodes: treeData,
    levels,
    keyForLevel,
    accessNode: (node) => ({
      id: node.id,
      name: node.name,
      depth: node.depth,
      children: node.children,
    }),
    buildItem: (base, node) => {
      const titles = normalizeSuggestedTitles(node.taxonomyTitles);
      return {
        ...base,
        ...(node.nodeCount !== undefined ? { nodeCount: node.nodeCount } : {}),
        ...(titles ? { rawTitles: titles } : {}),
      };
    },
  });

/**
 * Dashboard adapter over the shared {@link walkTaxonomyTreeToSelectionLists}
 * walk: walks normalized `TreeData` (carrying its own `depth`) and enriches each
 * item with `nodeCount` and normalized `rawTitles`. `keyForLevel` defaults to the
 * BE level tag (right for rics/geo); role passes an id-based resolver so it
 * doesn't depend on the BE tag strings (see treeToRoleSelectionLists).
 * Memoized per input identity and shared between callers — treat as read-only.
 */
export const treeToParentLinkedSelectionLists = (
  treeData: readonly TreeData[],
  levels: readonly TaxonomyLevelMetadata[],
  keyForLevel: LevelKeyResolver = keyByLevelTag
): ParentLinkedSelectionLists => {
  let byLevels = selectionListsByTree.get(treeData);
  if (!byLevels) {
    byLevels = new WeakMap();
    selectionListsByTree.set(treeData, byLevels);
  }
  let byResolver = byLevels.get(levels);
  if (!byResolver) {
    byResolver = new WeakMap();
    byLevels.set(levels, byResolver);
  }
  const cached = byResolver.get(keyForLevel);
  if (cached) return cached;

  const lists = walkTree(treeData, levels, keyForLevel);
  if (process.env.NODE_ENV !== 'production') freezeSelectionLists(lists);
  byResolver.set(keyForLevel, lists);
  return lists;
};
